import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { IMaskInput } from "react-imask";
import TableAllData from "components/Content/CustomTable/TableAllData";
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls, brokerApiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { Utils } from "common/Utils";
import AximTooltip from "components/AximTooltip";
import PdfViewerModal from "components/PdfViewerModal";
import StatusDetailsDialog from "components/Content/Modal/StatusDetailsDialog";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { LoadingButton } from "@mui/lab";

const TextMaskCustom = React.forwardRef((props, ref) => {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            inputRef={ref}
            onAccept={value => onChange(value)}
            mask="00-0000000"
            overwrite
        />
    );
});

export default function LogiqMindManager () {
    const { currentPortal, companyId, error } = useContext(PrivateRoutesContext);
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [doShowStatusDetails, setDoShowStatusDetails] = useState(false);
    const [statusTitle, setStatusTitle] = useState("");
    const [statuses, setStatuses] = useState([]);

    const [doShowRequest, setDoShowRequest] = useState(false);
    const [isReadonly, setIsReadonly] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [companies, setCompanies] = useState([]);
    const [ein, setEIN] = useState("");
    const [company, setCompany] = useState({});
    const blankCompany = { companyName: "", address1: "", address2: "", city: "", stateId: "", zipcode: "", countryId: "", }

    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    
    const [reportId, setReportId] = useState('');
    const [document, setDocument] = useState(null);
    const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

    const isEmployer = useMemo(() => {
        console.log(currentPortal);
        return currentPortal === "EMPLOYER";
    },[currentPortal])

    const filter = createFilterOptions();

    const columns = [
    {
        Header: "",
        accessor: "logIqmindReportId",
        Cell: e =>
            <>
                {!isEmployer ?
                    <AximTooltip title="View Request">
                        <IconButton onClick={() => showRequest(`${e.value}`)} color="primary"><VisibilityOutlinedIcon /></IconButton>
                    </AximTooltip> : null
                }
                {e.row.original.reportPath ?
                    <AximTooltip title="View Report">
                        <IconButton onClick={() => showLogIQmindReport(`${e.value}`)} color="primary"><ArticleOutlinedIcon /></IconButton>
                    </AximTooltip> : null
                }
            </>,
        disableFilters: true,
    },
    {
        Header: "Company Name",
        accessor: "companyName",
    },
    {
        Header: "Requestor Name",
        accessor: "requestorName",
    },
    {
        Header: "Request Date",
        accessor: (cell) => Utils.formatDate(cell.requestDate),
        Cell: ({row}) => (
          <Box>
            {Utils.formatDate(row.original.requestDate)}
          </Box>
        ),
        sortType: "dateSort"
    },
    {
        Header: "Create Date",
        accessor: (cell) => Utils.formatDate(cell.createDate),
        Cell: ({row}) => (
            <Box>
                {Utils.formatDate(row.original.createDate)}
            </Box>
        ),
        sortType: "dateSort"
    },
    {
        Header: () => {
          return (
            <>
              Status
              <IconButton onClick={showStatuses} className="py-0" color="primary"><ListAltOutlinedIcon /></IconButton>
            </>
          )
        },
        accessor: "status",
        Cell: ({row}) => (
            <Box className="d-flex" justifyContent="space-between">
                <Box>
                    {row.original.status}
                </Box>
                {row.original.statusComment ?
                    <IconButton onClick={() => showComment(row.original)} color="primary">
                        <InfoOutlinedIcon />
                    </IconButton> : null
                }
            </Box>
        )
    }];

    const showStatuses = e => {
      e.stopPropagation();

      setStatusTitle("LogIQmind Report Statuses");
      setDoShowStatusDetails(true);
    }

    useEffect(() => {
        getStates();
        getCountries();

        apiGet(apiUrls.getStatuses("LOGIQMINDREPORT"))
            .then((response) => {
                setStatuses(response.data);
            })
            .catch(function (e) {
                setStatuses([]);
            });
    },[])

    useEffect(() => {
        loadReports();
        if (!isEmployer) {
            getBrokerCompanies();
        }
        getStates();
        getCountries();
    }, [isEmployer, companyId]);

    const isSubmitDisabled = useMemo(() => {
        var einRegex = /^\d{2}-\d{7}$/;

        return !(einRegex.test(ein) || (company && company.companyName && company.address1 && company.city && company.zipcode))
    },[ein, company])

    const showComment = (request) => {
        error.setTitle("Status Comment");
        error.setText(request.statusComment);
        error.setDoShowError(true);
    }

    const loadReports = () => {
        setIsLoading(true);

        var url = brokerApiUrls.getBrokerLogIQmindReports;
        if (isEmployer) {
            url = apiUrls.getLogIQmindReports(companyId);
        }

        apiGet(url).then(
            (response) => {
                // debugger
                setReports(response.data);
            }
        )
        .finally(() => setIsLoading(false));
    }

    const handleSubmit = () => {
        setIsSubmitting(true);
        setIsReadonly(true);

        var stateAbbr = ''
        if (company.stateId) {
            var selectedState = states.find(s => s.stateId === company.stateId);
            if (selectedState) {
                stateAbbr = selectedState.stateAbbr;
            }
        }

        var countryCode = '';
        if (company.countryId) {
            var selectedCountry = countries.find(c => c.countryId === company.countryId);
            if (selectedCountry) {
                countryCode = selectedCountry.countryCode;
            }
        }

        apiPost(brokerApiUrls.submitLogIQmindRequest, {
            ein: ein,
            companyId: company.companyId,
            companyName: company.companyName,
            address1: company.address1,
            address2: company.address2,
            city: company.city,
            stateId: company.stateId === "" ? null : company.stateId,
            state: stateAbbr,
            zipcode: company.zipcode,
            countryId: company.countryId === "" ? null : company.countryId,
            country: countryCode,
        })
        .catch((e) => {
            error.setTitle("An Error Occurred");
            error.setText("Unable to submit request.");
            error.setDoShowError(true);
        })
        .finally(() => {
            loadReports();
            setDoShowRequest(false);
            setIsSubmitting(false);
            setIsReadonly(true);
        });
    }

    const showLogIQmindReport = (reportId) => {
        apiGet(apiUrls.getLogIQmindReport(reportId))
            .then((response) => {
                setDocument(response.data);
                setReportId(reportId)
            })
            .catch((e) => {
                error.setTitle("An Error Occurred");
                error.setText("Unable to display report.");
                error.setDoShowError(true);
            })
            .finally(() => {
                setIsPdfViewerOpen(true);
            });
    }

    const showRequest = (reportId) => {
        var report = reports.find(r => r.logIqmindReportId === reportId);

        if (report?.request) {
            setEIN(report.request.ein);

            var c = {
                companyName: report.request.companyName,
                address1: report.request.address1,
                address2: report.request.address2,
                city: report.request.city,
                stateId: report.request.stateId,
                zipcode: report.request.zipcode,
                countryId: report.request.countryId,
            }

            setCompany(c);

            setDoShowRequest(true);
            setIsReadonly(true);
        }
    }

    const handleCreateRequest = () => {
        setEIN("");
        setCompany(blankCompany);
        
        setDoShowRequest(true);
        setIsReadonly(false);
    }

    const getBrokerCompanies = () => {
        apiGet(brokerApiUrls.getBrokerCompanies)
            .then((response) => {
                if (response.data) {
                    setCompanies(response.data);
                }
                else {
                    setCompanies([]);
                }
            })
            .catch((e) => {
                error.setTitle("An Error Occurred");
                error.setText("Unable to load companies.");
                error.setDoShowError(true);
            })
            .finally(() => {
                
            });
    }

    const getStates = () => {
        var statesList = localStorage.getItem("statesList");

        if (statesList) {
            var statesListObj = JSON.parse(statesList);
            setStates(statesListObj);
        }
        else {
            apiGet(apiUrls.getStates()).then(
                (lovResponse) => {
                    setStates(lovResponse.data)
                    localStorage.setItem("statesList", JSON.stringify(lovResponse.data));
                }
            )
        }
    }

    const getCountries = () => {
        var countryList = localStorage.getItem("countriesList");

        if (countryList) {
            var countryListObj = JSON.parse(countryList);
            setCountries(countryListObj);
        }
        else {
            apiGet(apiUrls.getCountries()).then(
                (lovResponse) => {
                    setCountries(lovResponse.data)
                    localStorage.setItem("countriesList", JSON.stringify(lovResponse.data));
                }
            )
        }
    }

    const requiredLabel = (text) => {
        return (
            isReadonly ? text :
            <Box className="d-inline-flex">
                <Box>
                    {text}
                </Box>
                <Box className="ms-1" color="red">
                    *
                </Box>
            </Box>
        )
    }

  return (
    <div >
        <TableAllData
            columns={columns}
            initialState={{ sortBy: [{ id: 'Request Date', desc: true }]}}
            data={reports}
            isLoading={isLoading}
            download={{ prefix: "LogIQmind", table: "LogIQmind" }}
            headerContents={!isEmployer ?
                <AximTooltip title="Request Report" disableFocusListener>
                    <IconButton color="primary" aria-label="Request Report" component="span"
                        onClick={handleCreateRequest} >
                        <PostAddOutlinedIcon />
                    </IconButton>
                </AximTooltip> : null}
        />

        <StatusDetailsDialog doShow={doShowStatusDetails} handleClose={() => setDoShowStatusDetails(false)} title={statusTitle} statuses={statuses} />

        <Dialog
            open={doShowRequest}
            maxWidth="md"
            fullWidth={true}
        >
            <DialogTitle id="dialog-title">Request New Report</DialogTitle>
            <DialogContent>
                <Box component="h6">Please provide the Employer Identification Number (EIN) or Name and Address of the Company for which the report is requested</Box>
                <Box>
                    <TextField
                        variant="standard"
                        fullWidth
                        label={requiredLabel("EIN (00-0000000)")}
                        value={ein}
                        onChange={value => setEIN(value)}
                        InputLabelProps={{ shrink: true, }}
                        readOnly={isReadonly}
                        disabled={isReadonly}
                        InputProps={{
                            inputComponent: TextMaskCustom
                        }}
                    />
                </Box>

                <Box component="h6" color="grey" className="my-4" sx={{ width: "100%", textAlign: "center" }}>- OR -</Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            value={company.companyName}
                            onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                    setCompany({ companyName: newValue});
                                } else if (newValue && newValue.inputValue) {
                                    setCompany({ ...blankCompany, companyName: newValue.inputValue });
                                } else if (newValue === null) {
                                    setCompany(blankCompany);
                                }
                                else {
                                    setCompany(newValue);
                                }
                            }}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);
                        
                                const { inputValue } = params;
                                // Suggest the creation of a new value
                                const isExisting = options.some((option) => inputValue === option.companyName);
                                if (inputValue !== '' && !isExisting) {
                                    filtered.push({
                                        inputValue,
                                        companyName: `Add "${inputValue}"`,
                                    });
                                }
                        
                                return filtered;
                            }}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            id="company-name"
                            fullWidth
                            disabled={isReadonly}
                            options={companies}
                            getOptionLabel={(option) => {
                                // Value selected with enter, right from the input
                                if (typeof option === 'string') {
                                  return option;
                                }
                                // Add "xxx" option created dynamically
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                // Regular option
                                return option.companyName;
                            }}
                            renderOption={(props, option) => <li {...props}>{option.companyName}</li>}
                            freeSolo
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label={requiredLabel("Company Name")}
                                    InputLabelProps={{ shrink: true, }}
                                />}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            variant="standard"
                            label={requiredLabel("Address Line 1")}
                            fullWidth
                            value={company.address1}
                            onChange={e => setCompany(c => { return {...c, address1: e.target.value }; })}
                            InputLabelProps={{ shrink: true, }}
                            InputProps={{
                              readOnly: isReadonly,
                              disabled: isReadonly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Address line 2"
                            variant="standard"
                            fullWidth
                            value={company.address2}
                            onChange={e => setCompany(c => { return {...c, address2: e.target.value }; })}
                            InputLabelProps={{ shrink: true, }}
                            InputProps={{
                              readOnly: isReadonly,
                              disabled: isReadonly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField
                            label={requiredLabel("City")}
                            variant="standard"
                            fullWidth
                            value={company.city}
                            onChange={e => setCompany(c => { return {...c, city: e.target.value }; })}
                            InputLabelProps={{ shrink: true, }}
                            InputProps={{
                              readOnly: isReadonly,
                              disabled: isReadonly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <FormControl variant="standard" fullWidth readOnly={isReadonly} disabled={isReadonly}>
                            <InputLabel shrink={true}>State/Province/Region</InputLabel>
                            <Select
                                value={company.stateId}
                                onChange={e => setCompany(c => { return {...c, stateId: e.target.value }; })}
                            >
                                <MenuItem key="" value="">Select State</MenuItem>
                                {states && states.length > 0 ? 
                                    states.map(s => 
                                        <MenuItem key={s.stateId} value={s.stateId}>{s.stateAbbr}</MenuItem>
                                    ) : null
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label={requiredLabel("Zip / Postal code")}
                            variant="standard"
                            fullWidth
                            value={company.zipcode}
                            onChange={e => setCompany(c => { return {...c, zipcode: e.target.value }; })}
                            InputLabelProps={{ shrink: true, }}
                            InputProps={{
                              readOnly: isReadonly,
                              disabled: isReadonly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <FormControl variant="standard" fullWidth readOnly={isReadonly} disabled={isReadonly}>
                            <InputLabel shrink={true}>Country</InputLabel>
                            <Select
                                value={company.countryId}
                                onChange={e => setCompany(c => { return {...c, countryId: e.target.value }; })}
                            >
                                <MenuItem key="" value="">Select Country</MenuItem>
                                {countries && countries.length > 0 ? 
                                    countries.map(c => 
                                        <MenuItem key={c.countryId} value={c.countryId}>{c.countryCode}</MenuItem>
                                    ) : null
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    {!isReadonly ?
                    <Grid item xs={12} >
                        <Box className="d-inline-flex" sx={{ fontWeight: 600}}>
                            <Box color="red">*</Box>
                            <Box className="ms-1">Required</Box>
                        </Box>
                    </Grid> : null}
                </Grid>
            </DialogContent>

            <DialogActions>
                {!isReadonly ?
                    <LoadingButton
                        onClick={handleSubmit}
                        color="primary"
                        variant="outlined"
                        disabled={isSubmitDisabled}
                        loading={isSubmitting}
                        loadingPosition="start"
                        startIcon={<PublishOutlinedIcon />}
                    >
                        Request
                    </LoadingButton> : null}
                <Button onClick={() => setDoShowRequest(false)} color="primary" variant="outlined" disabled={isSubmitting}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>

        {document ?
            <PdfViewerModal
                open={isPdfViewerOpen}
                documentId={document.documentId}
                filename={document.filename}
                description={document.description}
                contents={document.contents}
                handleClose={() => {
                    setIsPdfViewerOpen(false);
                    setDocument(null);
                }}
                handleDownloadUrl={apiUrls.downloadLogIQmindReport(reportId, document.documentId)}
            />
        : null}
    </div>
  );
}
